const STORAGE_KEYS = {
  draft: 'randflow-v2-draft',
  language: 'randflow-language',
  lastResult: 'randflow-v2-last-result',
  lastResultSession: 'randflow-v2-session-result',
  offlineQueue: 'randflow-v2-offline-queue'
};

const LOCAL_RESULT_TTL_HOURS = 24;

function readJson(key, fallback = null) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    return false;
  }
}

function removeItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // Local storage is best-effort in private or restricted browsers.
  }
}

function saveDraft(draft) {
  return writeJson(STORAGE_KEYS.draft, {
    ...draft,
    savedAt: new Date().toISOString()
  });
}

function loadDraft() {
  return readJson(STORAGE_KEYS.draft);
}

function clearDraft() {
  removeItem(STORAGE_KEYS.draft);
}

function saveLanguage(language) {
  try {
    localStorage.setItem(STORAGE_KEYS.language, language);
  } catch (error) {
    // Language falls back to Portuguese if storage is unavailable.
  }
}

function loadLanguage() {
  try {
    return localStorage.getItem(STORAGE_KEYS.language);
  } catch (error) {
    return null;
  }
}

function isExpired(record) {
  return record && record.expiresAt && Date.parse(record.expiresAt) <= Date.now();
}

function saveLastResult(result, signature, options = {}) {
  const savedAt = new Date();
  const payload = {
    result,
    signature,
    savedAt: savedAt.toISOString(),
    expiresAt: new Date(savedAt.getTime() + LOCAL_RESULT_TTL_HOURS * 60 * 60 * 1000).toISOString(),
    persistence: options.sessionOnly ? 'session' : 'local'
  };

  if (options.sessionOnly && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(STORAGE_KEYS.lastResultSession, JSON.stringify(payload));
      removeItem(STORAGE_KEYS.lastResult);
      return true;
    } catch (error) {
      return false;
    }
  }

  return writeJson(STORAGE_KEYS.lastResult, payload);
}

function loadLastResult() {
  let record = readJson(STORAGE_KEYS.lastResult);

  if ((!record || isExpired(record)) && typeof sessionStorage !== 'undefined') {
    try {
      const value = sessionStorage.getItem(STORAGE_KEYS.lastResultSession);
      record = value ? JSON.parse(value) : record;
    } catch (error) {
      record = null;
    }
  }

  if (isExpired(record)) {
    clearLastResult();
    return null;
  }

  return record;
}

function clearLastResult() {
  removeItem(STORAGE_KEYS.lastResult);
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEYS.lastResultSession);
    }
  } catch (error) {
    // Session storage is best-effort.
  }
}

function enqueueOfflineEvent(event) {
  const queue = readJson(STORAGE_KEYS.offlineQueue, []);
  const nextQueue = [
    ...queue,
    {
      queuedAt: new Date().toISOString(),
      online: typeof navigator === 'undefined' ? null : navigator.onLine,
      event
    }
  ].slice(-250);
  writeJson(STORAGE_KEYS.offlineQueue, nextQueue);
  return nextQueue;
}

function loadOfflineQueue() {
  return readJson(STORAGE_KEYS.offlineQueue, []);
}

function clearOfflineQueue() {
  removeItem(STORAGE_KEYS.offlineQueue);
}

module.exports = {
  STORAGE_KEYS,
  LOCAL_RESULT_TTL_HOURS,
  clearDraft,
  clearLastResult,
  clearOfflineQueue,
  enqueueOfflineEvent,
  loadDraft,
  loadLanguage,
  loadLastResult,
  loadOfflineQueue,
  saveDraft,
  saveLanguage,
  saveLastResult
};
